import React, { useEffect, useState, useCallback } from "react";
import {
  Space,
  Row,
  Col,
  notification,
  Table,
  TableColumnsType,
  Typography,
  Button,
  message,
  Spin,
  Tag,
  Progress,
  Tooltip,
  Tabs,
  Statistic,
  Empty,
  Modal,
  Dropdown,
  Menu,
} from "antd";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import {
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  ToolOutlined,
  FileTextOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  FileSearchOutlined,
  RocketOutlined,
  MoreOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { FinetuneDataset, FinetuneRunNew } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import RequestViewModal from "../../Request/RequestViewModal";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";
import DatasetModification from "./DatasetModification";
import { MultiLayerSemanticChart } from "../../shared/MultiLayerSemanticChart";
import { getTimeRemaining } from "../FinetuneRun/FinetuneRuns";
import AIReview from "./AIReview";
import { RequestViewButton } from "../../shared/button/Buttons";

interface AIReviewPayload {
  correction_type: string;
  choices?: string[];
}

const { TabPane } = Tabs;

const DatasetDetail: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [finetuneRuns, setFinetuneRuns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [isRunsLoading, setIsRunsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(10);
  const [runsCurrentPage, setRunsCurrentPage] = useState<number>(1);
  const [runsPageSize, setRunsPageSize] = useState<number>(10);
  const [totalRuns, setTotalRuns] = useState<number>(0);
  const [runsByPage, setRunsByPage] = useState<Record<number, any[]>>({});
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [isModificationModalVisible, setIsModificationModalVisible] =
    useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ChatCompletionRequest | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [isAiReviewModalVisible, setIsAiReviewModalVisible] = useState(false);
  const [isAiReviewLoading, setIsAiReviewLoading] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [isReviewsLoading, setIsReviewsLoading] = useState<boolean>(false);
  const [reviewsCurrentPage, setReviewsCurrentPage] = useState<number>(1);
  const [reviewsPageSize, setReviewsPageSize] = useState<number>(10);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [reviewsByPage, setReviewsByPage] = useState<Record<number, any[]>>({});
  const [augmentations, setAugmentations] = useState<any[]>([]);
  const [isAugmentationsLoading, setIsAugmentationsLoading] = useState<boolean>(false);
  const [augmentationsCurrentPage, setAugmentationsCurrentPage] = useState<number>(1);
  const [augmentationsPageSize, setAugmentationsPageSize] = useState<number>(10);
  const [totalAugmentations, setTotalAugmentations] = useState<number>(0);
  const [reviewProgress, setReviewProgress] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
    tabIcon: {
      marginRight: "8px",
    },
    aiIconStyle: {
      width: "12px",
      height: "12px",
      color: "#8c8c8c",
      marginLeft: "4px",
      verticalAlign: "text-top",
    },
  };

  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  // Add global CSS for interactive rows
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button {
        padding: 0 !important;
        height: auto !important;
        line-height: 1.5715 !important;
        border: none !important;
        background: none !important;
        box-shadow: none !important;
        color: #52c41a;
      }
      .details-button:hover {
        color: #73d13d !important;
        background: none !important;
        box-shadow: none !important;
        transform: none !important;
      }
      .details-button[disabled] {
        color: #d9d9d9 !important;
        cursor: not-allowed;
      }
      .details-button[disabled]:hover {
        color: #d9d9d9 !important;
      }
      .ant-table-thead > tr > th {
        background: #f6ffed !important;
      }
      .ant-table-thead > tr > th::before {
        display: none !important;
      }
      .ant-pagination {
        padding: 16px 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;
      }
      .ant-pagination-options {
        margin-left: 16px !important;
      }
      .ant-select-selector {
        border-radius: 6px !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-statistic-content-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
      .ant-modal-content {
        border-radius: 12px;
        overflow: hidden;
      }
      .ant-table {
        border-radius: 8px;
        overflow: hidden;
      }
      .ant-statistic-title {
        margin-bottom: 4px !important;
      }
      .ant-tag {
        border-radius: 4px;
        padding: 3px 8px;
      }
      .dataset-detail-container .ant-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
      }
      .dataset-detail-container .ant-btn {
        transition: all 0.2s ease;
      }
      .custom-tabs .ant-tabs-tab {
        transition: all 0.3s ease;
        border-radius: 6px 6px 0 0 !important;
        padding: 8px 16px !important;
      }
      .custom-tabs .ant-tabs-tab:hover {
        color: #1890ff;
      }
      .custom-tabs .ant-tabs-tab-active {
        background-color: #f0f7ff !important;
      }
      /* Fix statistic icon alignment */
      .ant-statistic-content {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-prefix {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-value {
        display: inline-flex;
        align-items: center;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch dataset details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      DatasetService.getDataset(Number(id), setIsLoading, apiKey).subscribe(
        (data: FinetuneDataset) => {
          if (data) {
            setDataset(data);
          } else {
            console.error("Received empty dataset data");
            notification.error({
              message: "Error",
              description: "Failed to fetch dataset: Empty response received",
            });
          }
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch dataset: ${error}`,
          });
        }
      );
    }
  }, [id, apiKey]);

  // Fetch finetune runs for the dataset
  useEffect(() => {
    if (id && activeTab === "runs" && !runsByPage[runsCurrentPage]) {
      fetchFinetuneRuns(runsCurrentPage);
    } else if (activeTab === "runs" && runsByPage[runsCurrentPage]) {
      setFinetuneRuns(runsByPage[runsCurrentPage]);
    }
  }, [id, apiKey, runsCurrentPage, runsPageSize, runsByPage, activeTab]);

  const fetchFinetuneRuns = (page: number) => {
    if (!id) return;

    setIsRunsLoading(true);
    const offset = (page - 1) * runsPageSize;

    FinetuneRunService.getFinetuneRunsByDatasetId(
      Number(id),
      offset,
      runsPageSize,
      setIsRunsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setFinetuneRuns(response.items || []);
        setTotalRuns(response.total);
        setRunsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
      },
      (error: any) => {
        console.error("Error fetching finetune runs:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch finetune runs: ${error}`,
        });
      }
    );
  };

  // Fetch dataset requests
  useEffect(() => {
    if (id && activeTab === "requests" && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else if (activeTab === "requests" && requestsByPage[currentPage]) {
      setRequests(requestsByPage[currentPage]);
    }
  }, [id, currentPage, requestPageSize, requestsByPage, activeTab]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);

    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      requestPageSize,
      setIsRequestsLoading,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching dataset requests:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  // Fetch dataset reviews
  useEffect(() => {
    if (id && activeTab === "reviews" && !reviewsByPage[reviewsCurrentPage]) {
      fetchReviews(reviewsCurrentPage);
    } else if (activeTab === "reviews" && reviewsByPage[reviewsCurrentPage]) {
      setReviews(reviewsByPage[reviewsCurrentPage]);
    }
  }, [id, apiKey, reviewsCurrentPage, reviewsPageSize, activeTab]);

  const fetchReviews = (page: number) => {
    if (!id) return;

    setIsReviewsLoading(true);
    const offset = (page - 1) * reviewsPageSize;

    DatasetService.getDatasetReviews(
      Number(id),
      offset,
      reviewsPageSize,
      setIsReviewsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setReviews(response.items || []);
        setTotalReviews(response.total);
        setReviewsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
      },
      (error: any) => {
        console.error("Error fetching dataset reviews:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch reviews: ${error}`,
        });
      }
    );
  };

  const getStatusTagColor = (status: string): string => {
    switch (status?.toUpperCase()?.trim()) {
      case "CREATED":
        return "default";
      case "RUNNING":
      case "PROCESSING":
        return "processing";
      case "COMPLETED":
      case "COMPLETE":
        return "success";
      case "FAILED":
      case "ERROR":
        return "error";
      default:
        return "default";
    }
  };

  const requestColumns: TableColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Tags
          <AiStars style={styles.aiIconStyle} />
        </div>
      ),
      key: "tags",
      width: "50%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Space wrap size={4}>
              {tags.map((tag: string) => (
                <Tag key={tag} color="blue" style={styles.tagStyle}>
                  {tag}
                </Tag>
              ))}
              {tags.length === 0 && (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Space>
          );
        } catch (e) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "20%",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        const shortId =
          requestId.length > 12
            ? `${requestId.substring(0, 6)}...${requestId.substring(
                requestId.length - 6
              )}`
            : requestId;

        return (
          <Space>
            <Typography.Text style={styles.monospaceFont} copyable={false}>
              {shortId}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success({
                  content: "Request ID copied to clipboard",
                  icon: <CopyOutlined style={{ color: "#52c41a" }} />,
                });
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record: ChatCompletionRequest) => (
        <RequestViewButton
          apiKey={apiKey}
          request={record}
          onClick={() => handleViewRequest(record)}>
          Details
        </RequestViewButton>
      ),
    },
  ];

  // Updated finetuning runs columns to match TestSetDetail table styling
  const finetuningRunsColumns: TableColumnsType<any> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Name",
      key: "name",
      width: "20%",
      render: (_, record) => (
        <div style={{ fontWeight: 500, color: "#1890ff" }}>
          {record.display_name}
        </div>
      ),
    },
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
      width: "20%",
      render: (text) => (
        <Tag color="purple" style={styles.tagStyle}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "30%",
      render: (_, record) => {
        const statusUpper = record.status.toUpperCase();

        if (statusUpper === "RUNNING") {
          const percent = record.progress
            ? Math.round(record.progress * 100)
            : 0;
          return (
            <Tooltip title={getTimeRemaining(record)}>
              <Progress
                percent={Number(percent.toFixed(1))}
                size="small"
                status="active"
                style={{ width: "100%" }}
              />
            </Tooltip>
          );
        } else if (statusUpper === "COMPLETED") {
          return (
            <Tag color="success" style={styles.tagStyle}>
              COMPLETED
            </Tag>
          );
        } else {
          return (
            <Tag
              color={getStatusTagColor(record.status)}
              style={styles.tagStyle}>
              {statusUpper}
            </Tag>
          );
        }
      },
    },
    {
      title: "Accuracy",
      key: "accuracy",
      width: "15%",
      render: (_, record: FinetuneRunNew) => {
        const getScoreColor = (score: number) => {
          if (score >= 80) return "green"; // green
          if (score >= 60) return "yellow"; // yellow
          return "red"; // red
        };
        if (record.validation_accuracy === null) return "-";
        return (
          <Tag color={getScoreColor(record.validation_accuracy)}>
            {record.validation_accuracy.toFixed(2)}%
          </Tag>
        );
      },
    },
  ];

  const reviewsColumns: TableColumnsType<any> = [
    {
      title: "Date Started",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Date Completed",
      dataIndex: "date_completed",
      key: "date_completed",
      width: "20%",
      render: (timestamp: number) => {
        if (!timestamp)
          return <Typography.Text type="secondary">—</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "# Reviews",
      dataIndex: "num_reviews",
      key: "num_reviews",
      width: "15%",
      render: (value: number) => (
        <Typography.Text strong>{value}</Typography.Text>
      ),
    },
    {
      title: "# Corrections",
      dataIndex: "num_corrections",
      key: "num_corrections",
      width: "15%",
      render: (value: number) => (
        <Typography.Text strong>{value}</Typography.Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (status: string, record: any) => {
        const progressData = reviewProgress[record.id.toString()];
        const currentStatus = progressData?.status || status;

        const statusConfig: Record<string, { color: string }> = {
          CREATED: { color: "default" },
          COMPLETED: { color: "success" },
          COMPLETE: { color: "success" },
          ERROR: { color: "error" },
          PENDING: { color: "processing" },
          PROCESSING: { color: "processing" },
        };

        if (currentStatus !== "RUNNING" && currentStatus !== "PROCESSING") {
          const completed_count =
            progressData?.corrected_count +
            progressData?.good_count +
            progressData?.error_count;
          const tooltipContent =
            (currentStatus === "ERROR" ||
              currentStatus === "COMPLETE" ||
              currentStatus === "COMPLETED") &&
            progressData
              ? `${completed_count}/${progressData.total_count} reviews completed (${progressData.corrected_count} corrected, ${progressData.good_count} good, ${progressData.error_count} errors)`
              : undefined;

          return (
            <Tooltip title={tooltipContent}>
              <Tag
                color={statusConfig[currentStatus]?.color || "default"}
                style={styles.tagStyle}>
                {currentStatus.toUpperCase()}
              </Tag>
            </Tooltip>
          );
        }

        if (!progressData) {
          return <Spin size="small" />;
        }

        const { total_count, corrected_count, good_count, error_count } =
          progressData;

        const completed_count = corrected_count + good_count + error_count;
        const percent =
          total_count > 0 ? (completed_count / total_count) * 100 : 0;

        return (
          <Tooltip
            title={`${completed_count}/${total_count} reviews completed (${corrected_count} corrected, ${good_count} good, ${error_count} errors)`}>
            <Progress
              percent={Number(percent.toFixed(1))}
              size="small"
              status="active"
              style={{ width: "100%" }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      align: "center",
      render: (_, record) => {
        const hasProcessingReview = reviews.some(
          (review) => review.status === "PROCESSING"
        );
        const isDisabled =
          record.status === "PROCESSING" ||
          record.status === "RUNNING" ||
          hasProcessingReview;

        const getTooltipTitle = () => {
          if (record.status === "PROCESSING" || record.status === "RUNNING") {
            return "Cannot retry while this review is still processing";
          }
          if (hasProcessingReview) {
            return "Cannot retry while another review is processing";
          }
          if (record.status === "ERROR") {
            return "Click to retry this failed review";
          }
          return "";
        };

        const menu = (
          <Menu>
            <Menu.Item
              key="retry"
              disabled={isDisabled}
              icon={<HistoryOutlined />}
              onClick={() => handleRetryReview(record.id)}>
              <Tooltip title={getTooltipTitle()}>
                <div style={{ display: "inline-block", width: "100%" }}>
                  Retry
                </div>
              </Tooltip>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button
              type="text"
              icon={<MoreOutlined />}
              className="details-button"
              style={{
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                padding: "0 10px",
                height: "32px",
                transition: "all 0.2s",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const augmentationsColumns: TableColumnsType<any> = [
    {
      title: "Original Request",
      dataIndex: "original_request_id",
      key: "original_request_id",
      width: "20%",
      render: (requestId: string) => {
        const shortId = requestId.length > 12
          ? `${requestId.substring(0, 6)}...${requestId.substring(requestId.length - 6)}`
          : requestId;

        return (
          <Space>
            <Typography.Text style={styles.monospaceFont} copyable={false}>
              {shortId}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success({
                  content: "Request ID copied to clipboard",
                  icon: <CopyOutlined style={{ color: "#52c41a" }} />,
                });
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Date Started",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Date Completed",
      dataIndex: "date_completed",
      key: "date_completed",
      width: "20%",
      render: (timestamp: number) => {
        if (!timestamp) return <Typography.Text type="secondary">—</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "# Augmentations",
      dataIndex: "request_count",
      key: "request_count",
      width: "15%",
      render: (value: number) => (
        <Typography.Text strong>{value}</Typography.Text>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "15%",
      render: (_, record: any) => {
        return (
          <Tag color={getStatusTagColor(record.status)} style={styles.tagStyle}>
            {record.status}
          </Tag>
        );
      }
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record) => {
        const isComplete = record.status === "COMPLETED";
        return (
          <Button
            type="link"
            onClick={() => navigate(`/models/datasets/${id}/augmentations/${record.augmentation_id}`)}
            className="details-button"
            disabled={!isComplete}
            style={{ color: !isComplete ? "#d9d9d9" : undefined }}>
            View
          </Button>
        );
      },
    },
  ];

  const handleStartAiReview = (payload: AIReviewPayload) => {
    if (!id) return;
    console.log("payload", payload);

    setIsAiReviewLoading(true);
    DatasetService.startAiReview(
      Number(id),
      setIsAiReviewLoading,
      apiKey,
      payload
    ).subscribe(
      (response: any) => {
        notification.success({
          message: "AI Review Started",
          description: `The AI review process has been initiated successfully. There were ${response.num_reviews} reviews to complete with ${response.num_corrections} corrections.`,
        });
        setIsAiReviewModalVisible(false);

        // Update the dataset locally to show review is in progress
        if (dataset) {
          setDataset({
            ...dataset,
            meta: {
              ...dataset.meta,
              review_status: "CREATED",
            },
          });
        }
      },
      (error: any) => {
        console.error("Error starting AI review:", error);
        notification.error({
          message: "Error",
          description: `Failed to start AI review: ${error}`,
        });
      }
    );
  };

  const handleRetryReview = (reviewId: number) => {
    Modal.confirm({
      title: "Retry Review",
      content: "Are you sure you want to retry this review?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        DatasetService.retryDatasetReview(reviewId, null, apiKey).subscribe(
          () => {
            notification.success({
              message: "Review Retry Started",
              description: "The review has been queued for retry.",
            });
            // Clear the reviews page cache to force a refresh
            setReviewsByPage({});
            // Reset to first page and fetch fresh data
            setReviewsCurrentPage(1);
            fetchReviews(1);
          },
          (error: any) => {
            console.error("Error retrying review:", error);
            notification.error({
              message: "Error",
              description: `Failed to retry review: ${error}`,
            });
          }
        );
      },
    });
  };

  useEffect(() => {
    const fetchProgress = () => {
      if (!id) return;

      DatasetService.getDatasetReviewProgressByDataset(
        Number(id),
        null,
        apiKey
      ).subscribe(
        (progressData: Record<string, any>) => {
          setReviewProgress(progressData);
        },
        (error: any) => {
          console.error(error);
        }
      );
    };

    // Always fetch progress once when component mounts
    fetchProgress();

    // Check if any reviews need monitoring
    const hasIncompleteReviews = reviews.some(
      (review) => review.status !== "COMPLETED" && review.status !== "ERROR"
    );

    // Set up interval only if there are incomplete reviews
    let intervalId: NodeJS.Timeout | undefined;
    if (hasIncompleteReviews) {
      intervalId = setInterval(fetchProgress, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [id, apiKey, reviews]);

  // Add useEffect for fetching augmentations
  const fetchAugmentations = useCallback((page: number) => {
    if (!id) return;
    const offset = (page - 1) * augmentationsPageSize;
    setIsAugmentationsLoading(true);

    DatasetService.getDatasetAugmentations(
      Number(id),
      offset,
      augmentationsPageSize,
      setIsAugmentationsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setAugmentations(response.items || []);
        setTotalAugmentations(response.total);
        setIsAugmentationsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching augmentations:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch augmentations: ${error}`,
        });
        setIsAugmentationsLoading(false);
      }
    );
  }, [id, augmentationsPageSize, apiKey]);

  useEffect(() => {
    if (id && activeTab === "augmentations") {
      fetchAugmentations(augmentationsCurrentPage);
    }
  }, [id, augmentationsCurrentPage, activeTab, fetchAugmentations]);

  // Add useEffect for polling augmentation status
  useEffect(() => {
    // Only poll if we're on the augmentations tab
    if (activeTab !== "augmentations") return;

    // Find any augmentations that are still running
    const runningAugmentations = augmentations.filter(
      aug => aug.status === "RUNNING" || aug.status === "CREATED"
    );

    // If no running augmentations, don't poll
    if (runningAugmentations.length === 0) return;

    const pollInterval = setInterval(() => {
      // For each running augmentation, check its status
      runningAugmentations.forEach(aug => {
        DatasetService.getAugmentationDetails(
          aug.augmentation_id,
          null,
          apiKey
        ).subscribe(
          (details: any) => {
            // If status changed to completed or error, refresh the table
            if (details.status === "COMPLETED" || details.status === "ERROR") {
              setAugmentationsCurrentPage(1);
              fetchAugmentations(1);
            }
          },
          (error: any) => {
            console.error("Error checking augmentation status:", error);
          }
        );
      });
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [activeTab, augmentations, apiKey, fetchAugmentations]);

  // Update useEffect to set initial tab from URL
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Stats Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <div style={styles.statsCard("#1890ff")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Total Requests
                </Typography.Text>
              }
              value={dataset?.request_count || 0}
              prefix={
                <FileTextOutlined
                  style={{
                    color: "#1890ff",
                    marginRight: "8px",
                    fontSize: "16px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#262626",
                lineHeight: "1.4",
              }}
              formatter={(value) => (
                <Tooltip title={value}>
                  <span>{value}</span>
                </Tooltip>
              )}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={styles.statsCard("#52c41a")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Date Created
                </Typography.Text>
              }
              value={moment(dataset?.date_created).format("MMM D, YYYY")}
              prefix={
                <CalendarOutlined
                  style={{
                    color: "#52c41a",
                    marginRight: "8px",
                    fontSize: "16px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#262626",
                lineHeight: "1.4",
              }}
              formatter={(value) => (
                <Tooltip title={value}>
                  <span>{value}</span>
                </Tooltip>
              )}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={styles.statsCard("#722ed1")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Finetune Runs
                </Typography.Text>
              }
              value={totalRuns}
              prefix={
                <RocketOutlined
                  style={{
                    color: "#722ed1",
                    marginRight: "8px",
                    fontSize: "16px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#262626",
                lineHeight: "1.4",
              }}
              formatter={(value) => (
                <Tooltip title={value}>
                  <span>{value}</span>
                </Tooltip>
              )}
            />
          </div>
        </Col>
      </Row>

      {/* Details Section */}
      <div style={styles.detailsContainer}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Details
          </Typography.Title>
        </div>
        <div style={styles.detailsContent}>
          <Row gutter={[0, 24]}>
            <Col span={24}>
              <Typography.Text style={styles.subtitle}>
                Description
              </Typography.Text>
              <Typography.Paragraph
                style={{
                  whiteSpace: "pre-wrap",
                  margin: 0,
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}>
                {dataset?.description || "No description provided"}
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <Typography.Text style={styles.subtitle}>Source</Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {dataset?.intent_group_name &&
                !dataset?.application_id &&
                !dataset?.intent_id ? (
                  <Link
                    to={`/intent-groups/${dataset?.intent_group_id}`}
                    style={{ color: "#1890ff" }}>
                    {dataset?.intent_group_name}
                  </Link>
                ) : dataset?.application_name &&
                  dataset?.intent_name &&
                  !dataset?.intent_group_id ? (
                  <>
                    <Link
                      to={`/application/${dataset?.application_id}`}
                      style={{ color: "#1890ff" }}>
                      {dataset?.application_name}
                    </Link>
                    {" / "}
                    <Link
                      to={`/application/${dataset?.application_id}/intent/${dataset?.intent_id}`}
                      style={{ color: "#1890ff" }}>
                      {dataset?.intent_name}
                    </Link>
                  </>
                ) : (
                  "Unknown source"
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Semantic Distribution Chart */}
      {dataset && (
        <MultiLayerSemanticChart
          chartData={[
            {
              entityType: "dataset",
              entityTypeId: id || "",
              name: dataset.name,
            },
          ]}
          apiKey={apiKey}
          title="Semantic Distribution"
        />
      )}
    </div>
  );

  const renderFinetuneRunsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Finetune Runs
          </Typography.Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/models/finetuning/new?dataset_id=${id}`)}>
            New Finetune Run
          </Button>
        </Col>
      </Row>

      <div style={styles.tableContainer}>
        <Table
          rowKey={(record) => record.id.toString()}
          columns={finetuningRunsColumns}
          dataSource={finetuneRuns}
          loading={isRunsLoading}
          rowClassName={() => "interactive-row"}
          onRow={(record) => ({
            onClick: () => navigate(`/models/finetuning/${record.id}`),
          })}
          pagination={{
            current: runsCurrentPage,
            pageSize: runsPageSize,
            total: totalRuns,
            onChange: (page, pageSize) => {
              setRunsCurrentPage(page);
              if (pageSize && pageSize !== runsPageSize) {
                setRunsPageSize(pageSize);
                setRunsByPage({});
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onShowSizeChange: (current, size) => {
              setRunsCurrentPage(1);
              setRunsPageSize(size);
              setRunsByPage({});
            },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No finetune runs found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Create a new finetune run to get started.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  const renderRequestsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Requests
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<ToolOutlined />}
              onClick={() => setIsModificationModalVisible(true)}>
              Broad Modification
            </Button>
          </Space>
        </Col>
      </Row>

      <div style={styles.tableContainer}>
        <Table
          dataSource={requests}
          columns={requestColumns}
          rowKey="request_id"
          loading={isRequestsLoading}
          rowClassName={() => "interactive-row"}
          pagination={{
            current: currentPage,
            pageSize: requestPageSize,
            total: totalRequests,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              if (pageSize && pageSize !== requestPageSize) {
                setRequestPageSize(pageSize);
                setRequestsByPage({});
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setRequestPageSize(size);
              setRequestsByPage({});
            },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No requests found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Add requests to this dataset to get started.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  const renderReviewsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Reviews
          </Typography.Title>
        </Col>
        <Col>
          <Tooltip
            title={
              dataset?.meta?.review_status === "CREATED"
                ? "An AI review has been created and is waiting to be scheduled"
                : dataset?.meta?.review_status === "SCHEDULED"
                ? "An AI review has been scheduled and is waiting to start"
                : dataset?.meta?.review_status === "PROCESSING"
                ? "An AI review is currently processing"
                : dataset?.meta?.review_status === "PENDING"
                ? "An AI review is in progress"
                : reviews.some((review) => review.status === "PROCESSING")
                ? "Cannot start new review while another review is processing"
                : ""
            }>
            <Button
              type="primary"
              icon={<AiStars style={{ fontSize: "14px" }} />}
              onClick={() => {
                const reviewStatus = dataset?.meta?.review_status as
                  | string
                  | undefined;
                const hasProcessingReview = reviews.some(
                  (review) => review.status === "PROCESSING"
                );
                if (
                  ["CREATED", "SCHEDULED", "PROCESSING", "PENDING"].includes(
                    reviewStatus || ""
                  ) ||
                  hasProcessingReview
                ) {
                  notification.info({
                    message: "Review In Progress",
                    description: hasProcessingReview
                      ? "Cannot start new review while another review is processing"
                      : `You already have an AI Review in status ${reviewStatus}`,
                  });
                } else {
                  setIsAiReviewModalVisible(true);
                }
              }}
              loading={isAiReviewLoading}
              disabled={
                ["CREATED", "SCHEDULED", "PROCESSING", "PENDING"].includes(
                  (dataset?.meta?.review_status as string) || ""
                ) || reviews.some((review) => review.status === "PROCESSING")
              }>
              AI Review
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <div style={styles.tableContainer}>
        <Table
          rowKey={(record) => record.id.toString()}
          columns={reviewsColumns}
          dataSource={reviews}
          loading={isReviewsLoading}
          rowClassName={() => "interactive-row"}
          pagination={{
            current: reviewsCurrentPage,
            pageSize: reviewsPageSize,
            total: totalReviews,
            onChange: (page, pageSize) => {
              setReviewsCurrentPage(page);
              if (pageSize && pageSize !== reviewsPageSize) {
                setReviewsPageSize(pageSize);
                setReviewsByPage({});
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onShowSizeChange: (current, size) => {
              setReviewsCurrentPage(1);
              setReviewsPageSize(size);
              setReviewsByPage({});
            },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No reviews found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Start an AI review to see results here.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  const renderAugmentationsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Augmentations
          </Typography.Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<ExperimentOutlined />}
            onClick={() => navigate(`/models/datasets/${id}/edit`)}>
            New Augmentation
          </Button>
        </Col>
      </Row>

      <div style={styles.tableContainer}>
        <Table
          rowKey={(record) => record.augmentation_id}
          columns={augmentationsColumns}
          dataSource={augmentations}
          loading={isAugmentationsLoading}
          rowClassName={() => "interactive-row"}
          pagination={{
            current: augmentationsCurrentPage,
            pageSize: augmentationsPageSize,
            total: totalAugmentations,
            onChange: (page, pageSize) => {
              setAugmentationsCurrentPage(page);
              if (pageSize && pageSize !== augmentationsPageSize) {
                setAugmentationsPageSize(pageSize);
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No augmentations found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Start a new augmentation to see results here.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="dataset-detail-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/models/datasets">Datasets</Link> / {dataset?.name}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                {dataset?.name}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage dataset requests, run fine-tuning, and review model
                improvements
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content with Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}
        tabBarExtraContent={
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/models/datasets/${id}/edit`)}>
            Edit Dataset
          </Button>
        }>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={styles.tabIcon} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>{renderOverviewTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <RocketOutlined style={styles.tabIcon} />
              Finetune Runs
            </span>
          }
          key="runs">
          <div style={styles.contentCard}>{renderFinetuneRunsTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileSearchOutlined style={styles.tabIcon} />
              Requests
            </span>
          }
          key="requests">
          <div style={styles.contentCard}>{renderRequestsTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileSearchOutlined style={styles.tabIcon} />
              Reviews
            </span>
          }
          key="reviews">
          <div style={styles.contentCard}>{renderReviewsTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <ExperimentOutlined style={styles.tabIcon} />
              Augmentations
            </span>
          }
          key="augmentations">
          <div style={styles.contentCard}>{renderAugmentationsTab()}</div>
        </TabPane>
      </Tabs>

      {/* Dataset Modification Modal */}
      {dataset && (
        <DatasetModification
          datasetId={Number(id)}
          isVisible={isModificationModalVisible}
          onClose={() => setIsModificationModalVisible(false)}
        />
      )}

      {/* Request View Modal */}
      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}

      {/* AI Review Confirmation Modal */}
      <AIReview
        isVisible={isAiReviewModalVisible}
        onClose={() => setIsAiReviewModalVisible(false)}
        onStartReview={handleStartAiReview}
        loading={isAiReviewLoading}
      />
    </div>
  );
};

export default DatasetDetail;
